import {Col, Form, Row} from 'react-bootstrap'
import {defineMessages, FormattedMessage, useIntl} from 'react-intl'
import {FormikErrors, FormikTouched} from 'formik'
import {InputCurrency} from 'app/tfi/components/InputCurrency'
import {DateInput} from 'app/tfi/components/DateInput'
import {TFormAllValues, TFormLifeDetail} from '../../../policies/policiesTypes'
import {PAYMENT_FREQUENCY_OPTIONS} from '../../../../helpers/policyFormKind'
import {normalizeAmount} from '../../../../helpers/consts'

/** Ids estáticos: el extractor de FormatJS no resuelve ids construidos con templates. */
const frequencyLabels = defineMessages({
  MONTHLY: {id: 'policies.detail.form.life.frequency.monthly', defaultMessage: 'Monthly'},
  QUARTERLY: {id: 'policies.detail.form.life.frequency.quarterly', defaultMessage: 'Quarterly'},
  YEARLY: {id: 'policies.detail.form.life.frequency.yearly', defaultMessage: 'Yearly'},
})

interface Props {
  values: TFormLifeDetail
  errors: FormikErrors<TFormAllValues>
  touched: FormikTouched<TFormAllValues>
  handleChange: (field: string, value: any) => void
  handleBlur: (field: string) => void
  /** Titular: de sus annual_incomeN sale el ingreso personal. */
  holder?: TFormAllValues['holder']
}

/**
 * Secciones A y B del Pre-Application Form de Vida: la propuesta (face amount,
 * frecuencia de pago, dueño de la póliza) y el seguro de vida ya vigente.
 *
 * Compañía, tipo de plan, prima y fecha efectiva NO viven aquí: se reutilizan las
 * columnas de Policy (carrier / plan_name / premium / effective_date).
 */
export const LifeProposalSection: React.FC<Props> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  holder,
}) => {
  const intl = useIntl()
  const selectOnePlaceholder = intl.formatMessage({
    id: 'common.placeholder.selectOption',
    defaultMessage: 'Select an option',
  })

  // El titular puede declarar hasta tres empleos; el ingreso personal es su suma.
  const personalIncome = [holder?.annualIncome1, holder?.annualIncome2, holder?.annualIncome3]
    .reduce((total, income) => total + (Number(normalizeAmount(income)) || 0), 0)
  const personalIncomeDisplay = personalIncome
    ? personalIncome.toLocaleString('en-US', {style: 'currency', currency: 'USD'})
    : '—'
  const lifeErrors = (errors.policyLifeDetail || {}) as FormikErrors<TFormLifeDetail>
  const lifeTouched = (touched.policyLifeDetail || {}) as FormikTouched<TFormLifeDetail>

  const ownerIsInsured = values.ownerIsInsured !== false
  const hasExisting = values.hasExistingLifeInsurance === true

  return (
    <>
      <div className='kt-heading kt-heading--md custom-border-brand mt-5'>
        <FormattedMessage
          id='policies.detail.form.life.proposalHeading'
          defaultMessage='Proposal information'
        />
      </div>
      <Row>
        <Col md={4}>
          <Form.Group>
            <Form.Label>
              <FormattedMessage
                id='policies.detail.form.life.faceAmount'
                defaultMessage='Face amount'
              /> <small className='text-danger'>
                <FormattedMessage id='common.hint.required' defaultMessage='(required)' />
              </small>
            </Form.Label>
            <InputCurrency
              value={normalizeAmount(values.faceAmount)}
              onValueChange={(v) => handleChange('faceAmount', v ?? '')}
            />
            {lifeErrors.faceAmount && lifeTouched.faceAmount && (
              <div className='text-danger small'>{lifeErrors.faceAmount}</div>
            )}
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>
              <FormattedMessage
                id='policies.detail.form.life.paymentFrequency'
                defaultMessage='Payment plan'
              /> <small className='text-danger'>
                <FormattedMessage id='common.hint.required' defaultMessage='(required)' />
              </small>
            </Form.Label>
            <Form.Select
              value={values.paymentFrequency || ''}
              onChange={(e) => handleChange('paymentFrequency', e.target.value)}
              onBlur={() => handleBlur('paymentFrequency')}
              isInvalid={Boolean(lifeErrors.paymentFrequency && lifeTouched.paymentFrequency)}
            >
              <option value=''>{selectOnePlaceholder}</option>
              {PAYMENT_FREQUENCY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {intl.formatMessage(frequencyLabels[opt.value as keyof typeof frequencyLabels])}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row className='mt-3'>
        <Col md={12}>
          <Form.Check
            type='switch'
            id='life-owner-is-insured'
            checked={ownerIsInsured}
            onChange={(e) => handleChange('ownerIsInsured', e.target.checked)}
            label={intl.formatMessage({
              id: 'policies.detail.form.life.ownerIsInsured',
              defaultMessage: 'The policy owner is the insured',
            })}
          />
        </Col>
      </Row>

      {/* Bloque del dueño: solo cuando difiere del asegurado (Owner Signature if Different). */}
      {!ownerIsInsured && (
        <Row className='mt-3'>
          <Col md={4}>
            <Form.Group>
              <Form.Label>
                <FormattedMessage
                  id='policies.detail.form.life.ownerName'
                  defaultMessage='Owner full name'
                />
              </Form.Label>
              <Form.Control
                value={values.ownerName || ''}
                onChange={(e) => handleChange('ownerName', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>
                <FormattedMessage
                  id='policies.detail.form.life.ownerRelationship'
                  defaultMessage='Relationship to the insured'
                />
              </Form.Label>
              <Form.Control
                value={values.ownerRelationship || ''}
                onChange={(e) => handleChange('ownerRelationship', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>
                <FormattedMessage id='policies.detail.form.life.ownerSsn' defaultMessage='Owner SSN' />
              </Form.Label>
              <Form.Control
                value={values.ownerSsn || ''}
                onChange={(e) => handleChange('ownerSsn', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className='mt-2'>
              <DateInput
                id='ownerDateBirth'
                name='ownerDateBirth'
                label={intl.formatMessage({
                  id: 'policies.detail.form.life.ownerDateBirth',
                  defaultMessage: 'Owner date of birth',
                })}
                value={values.ownerDateBirth || ''}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </Form.Group>
          </Col>
          <Col md={8}>
            <Form.Group>
              <Form.Label>
                <FormattedMessage
                  id='policies.detail.form.life.ownerAddress'
                  defaultMessage='Owner address'
                />
              </Form.Label>
              <Form.Control
                value={values.ownerAddress || ''}
                onChange={(e) => handleChange('ownerAddress', e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
      )}

      <div className='kt-heading kt-heading--md custom-border-brand mt-5'>
        <FormattedMessage
          id='policies.detail.form.life.existingHeading'
          defaultMessage='Existing life insurance'
        />
      </div>
      <Row>
        <Col md={12}>
          <Form.Check
            type='switch'
            id='life-has-existing'
            checked={hasExisting}
            onChange={(e) => handleChange('hasExistingLifeInsurance', e.target.checked)}
            label={intl.formatMessage({
              id: 'policies.detail.form.life.hasExisting',
              defaultMessage: 'Do you have life insurance in force?',
            })}
          />
        </Col>
      </Row>

      {hasExisting && (
        <Row className='mt-3'>
          <Col md={4}>
            <Form.Group>
              <Form.Label>
                <FormattedMessage
                  id='policies.detail.form.life.existingCarrier'
                  defaultMessage='Company'
                />
              </Form.Label>
              <Form.Control
                value={values.existingCarrierName || ''}
                onChange={(e) => handleChange('existingCarrierName', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>
                <FormattedMessage
                  id='policies.detail.form.life.existingPolicyNumber'
                  defaultMessage='Policy number'
                />
              </Form.Label>
              <Form.Control
                value={values.existingPolicyNumber || ''}
                onChange={(e) => handleChange('existingPolicyNumber', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>
                <FormattedMessage
                  id='policies.detail.form.life.existingPlanName'
                  defaultMessage='Plan'
                />
              </Form.Label>
              <Form.Control
                value={values.existingPlanName || ''}
                onChange={(e) => handleChange('existingPlanName', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>
                <FormattedMessage
                  id='policies.detail.form.life.existingFaceAmount'
                  defaultMessage='Face amount'
                />
              </Form.Label>
              <InputCurrency
                value={normalizeAmount(values.existingFaceAmount)}
                onValueChange={(v) => handleChange('existingFaceAmount', v ?? '')}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className='mt-2'>
              <DateInput
                id='existingEffectiveDate'
                name='existingEffectiveDate'
                label={intl.formatMessage({
                  id: 'policies.detail.form.life.existingEffectiveDate',
                  defaultMessage: 'Effective date',
                })}
                value={values.existingEffectiveDate || ''}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </Form.Group>
          </Col>
          <Col md={4} className='d-flex align-items-center'>
            <Form.Check
              type='switch'
              id='life-will-be-replaced'
              checked={values.willBeReplaced === true}
              onChange={(e) => handleChange('willBeReplaced', e.target.checked)}
              label={intl.formatMessage({
                id: 'policies.detail.form.life.willBeReplaced',
                defaultMessage: 'It is going to be replaced',
              })}
            />
          </Col>
        </Row>
      )}

      <div className='kt-heading kt-heading--md custom-border-brand mt-5'>
        <FormattedMessage
          id='policies.detail.form.life.financialHeading'
          defaultMessage='Financial information'
        />
      </div>
      <Row>
        <Col md={3}>
          <Form.Group>
            <Form.Label>
              <FormattedMessage
                id='policies.detail.form.life.personalIncome'
                defaultMessage='Personal income'
              />
            </Form.Label>
            {/* Suma de los ingresos del titular. Es de sólo lectura a propósito:
                el dato se carga en Titular › Empleo e ingresos, y tenerlo editable
                acá permitiría que los dos números divergieran. */}
            <Form.Control readOnly value={personalIncomeDisplay} />
            <Form.Text className='text-muted'>
              <FormattedMessage
                id='policies.detail.form.life.personalIncomeHint'
                defaultMessage="Sum of the holder's incomes"
              />
            </Form.Text>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>
              <FormattedMessage
                id='policies.detail.form.life.personalNetWorth'
                defaultMessage='Personal net worth'
              />
            </Form.Label>
            <InputCurrency
              value={normalizeAmount(values.personalNetWorth)}
              onValueChange={(v) => handleChange('personalNetWorth', v ?? '')}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>
              <FormattedMessage
                id='policies.detail.form.life.householdIncome'
                defaultMessage='Household income'
              />
            </Form.Label>
            <InputCurrency
              value={normalizeAmount(values.householdIncome)}
              onValueChange={(v) => handleChange('householdIncome', v ?? '')}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>
              <FormattedMessage
                id='policies.detail.form.life.householdNetWorth'
                defaultMessage='Household net worth'
              />
            </Form.Label>
            <InputCurrency
              value={normalizeAmount(values.householdNetWorth)}
              onValueChange={(v) => handleChange('householdNetWorth', v ?? '')}
            />
          </Form.Group>
        </Col>
      </Row>
    </>
  )
}
